import {
  getCustomVariables,
  getImage,
  getModelValue,
  getReportVirtual,
  getSurveyVariables,
  getTraits
} from '@aytm/app/stats/lib/stat-page'

import { removeTagsFromText } from '@aytm/app/stats/page/components/lib/utils'

// A concept text longer than this reads as a sentence rather than a label, and the table shows
// the full texts instead of truncating them.
const FULL_TEXT_LENGTH = 17

// The split-by reference addresses one of four collections, searched in the order the report
// resolves them: a survey trait, a survey / piping / custom variable, then a report-local
// variable. Each names its values differently, so the values come back normalised.
export const resolveSplitByValues = ref => {
  if (null == ref) return

  let source = getTraits().find(trait => ref === trait.id) || null
  let values = source?.values.map(value => ({ id: value.id, name: value.name }))

  source =
    source ||
    getSurveyVariables('variables')[ref] ||
    getSurveyVariables('pipingVariables')[ref] ||
    getCustomVariables()[ref]
  values = values || source?.values.map(value => ({ id: value, name: value }))

  source = source || getReportVirtual()[ref]
  values = values || source?.answerVariants.map(av => ({ id: `a${av.position}`, name: `A${av.position}: ${av.text}` }))

  return values
}

// Significance is answered per level pair: the outer key is the significance level, the inner one
// pairs it with the confidence level. Both come off the page, so a change to either re-resolves.
export const resolveSigtestKeys = () => {
  const confidenceLevel = getModelValue('confidenceLevel') || 0.95
  const significanceLevel = getModelValue('significanceLevel') || 0.9

  const level = `${Math.round(1000 * significanceLevel)}`

  return { level, pair: `${level}_${Math.round(1000 * confidenceLevel)}` }
}

// Copy the metric fields the table reads (percents / amount / sd / base / sigtest) from a source
// answer cell into the compacted target cell.
export const assignMetrics = (cell, src, attr, keys) => {
  if (Object.hasOwn(src, 'percents')) cell.percents = src.percents
  if (Object.hasOwn(src, 'amount')) {
    cell.amount = src.amount
    if (['means', 'meansReversed'].includes(attr.method)) cell.text = src.amount.toFixed(2)
  }
  if (Object.hasOwn(src, 'sd')) cell.sd = src.sd
  if (Object.hasOwn(src, 'base')) cell.base = src.base

  cell.sigtest = structuredClone(src.sigtest || {})?.[keys.pair] || []

  if (null != src.sigtestAvg && 'object' === typeof src.sigtestAvg)
    cell.sigtext = src.sigtestAvg[keys.level] || undefined
}

// One batch cell, with its split-by breakdown when the researcher asked for one. Writes the
// compacted answer as it goes -- the table draws off `batches`, the exporters off `answer`.
export const assignAnswerVariant = ({ compacted, answer, batchKey, attr, splitByRef, splitByValues, keys }) => {
  const attrKey = `a${attr.position}`
  const src = answer?.[batchKey]?.[attrKey] || {}

  const cell = (compacted[batchKey][attrKey] = {})
  assignMetrics(cell, src, attr, keys)

  if (null != splitByRef && Object.hasOwn(src, splitByRef)) {
    compacted[batchKey][splitByRef] = {}
    cell[splitByRef] = {}

    for (const value of splitByValues || []) {
      const split = src[splitByRef][value.id] || {}

      compacted[batchKey][splitByRef][value.id] = { total: split.total }

      const splitCell = (cell[splitByRef][value.id] = {})
      assignMetrics(splitCell, split, attr, keys)
    }
  }

  return { text: attr.text, position: attr.position }
}

// A concept-lab batch is one entity, carrying every attribute as an answer variant.
const calcBatches = ({ compacted, answer, entities, attributes, splitByRef, splitByValues, keys }) =>
  entities.map(item => {
    const batchKey = `b${item.position}`

    compacted[batchKey] = compacted[batchKey] || { total: answer?.[batchKey]?.total || null }

    if (null != splitByRef && Object.hasOwn(answer?.[batchKey] || {}, splitByRef)) {
      compacted[batchKey][splitByRef] = answer[batchKey][splitByRef]
    }

    const batch = {
      text: item.text,
      answerVariants: attributes.map(attr =>
        assignAnswerVariant({ compacted, answer, batchKey, attr, splitByRef, splitByValues, keys })
      ),
      position: item.position
    }

    if (null != item.image) batch.image = getImage(item.image)

    return batch
  })

// The compare-items payload a concept-lab partial draws: the entities as batches, the attributes
// as their answer variants, and the answer compacted to the cells the table and exporters read.
export const calcPartialProps = ({
  id,
  key,
  text,
  answer,
  entities,
  attributes,
  splitBy,
  features,
  keys = resolveSigtestKeys(),
  percentDecimals = getModelValue('percentDecimals')
}) => {
  const splitByRef = splitBy?.ref
  const splitByValues = resolveSplitByValues(splitByRef)

  const compacted = { total: answer?.total || null, loading: 'loading' === answer }

  const batches = calcBatches({
    compacted,
    answer,
    entities: entities || [],
    attributes: attributes || [],
    splitByRef,
    splitByValues,
    keys
  })

  return {
    id,
    key,
    title: `${key.toUpperCase()}: ${text}`,
    type: 'compare-items',
    format: {
      preset: 'all',
      attribute: 0,
      analysisMode: 'sigtest',
      itemName: 'concept',
      showImages: batches.some(batch => undefined !== batch.image),
      fullTexts: batches.some(batch => FULL_TEXT_LENGTH < removeTagsFromText(batch.text).length),
      showScrollToDetails: false,
      enableSplitBy: !features?.insightsMapping
    },
    answer: compacted,
    batches,
    splitBy: splitBy || { ref: null, group: 'items' },
    percentDecimals
  }
}
